import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Navbar from '@/components/Navbar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Search, Layers, Plus, Sparkles, Loader2, Download, Tag, X, ArrowRight, Repeat, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SchemasPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('edit_elements');
  const [schemas, setSchemas] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', description: '', tags: [] });
  const [tagInput, setTagInput] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tagEditId, setTagEditId] = useState(null);
  const [tagEditInput, setTagEditInput] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedTag) params.set('tag', selectedTag);
      if (search) params.set('search', search);
      const [sRes, tRes] = await Promise.all([api.get(`/schemas?${params}`), api.get('/schemas/tags')]);
      setSchemas(sRes.data);
      setAllTags(tRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search, selectedTag]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAISuggest = async () => {
    if (!form.name.trim()) return;
    setSuggesting(true);
    try {
      const r = await api.post('/schemas/ai-suggest', { name: form.name.trim() });
      setForm(p => ({ ...p, code: r.data.code || p.code, description: r.data.description || p.description }));
      toast.success('AI suggestions applied');
    } catch { toast.error('AI suggestion failed'); }
    finally { setSuggesting(false); }
  };

  const addFormTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags.includes(t)) setForm(p => ({ ...p, tags: [...p.tags, t] }));
    setTagInput('');
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error('Name and code required'); return; }
    setSaving(true);
    try {
      const r = await api.post('/schemas', form);
      toast.success('Schema created');
      setCreateOpen(false);
      fetchData();
      navigate(`/schemas/${r.data.schema_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const addTagToSchema = async (schemaId, currentTags) => {
    const t = tagEditInput.trim().toLowerCase();
    if (!t || currentTags.includes(t)) { setTagEditId(null); return; }
    try {
      await api.put(`/schemas/${schemaId}`, { tags: [...currentTags, t] });
      fetchData();
    } catch (err) { toast.error('Failed'); }
    setTagEditId(null);
    setTagEditInput('');
  };

  const removeTagFromSchema = async (schemaId, currentTags, tagToRemove) => {
    try {
      await api.put(`/schemas/${schemaId}`, { tags: currentTags.filter(t => t !== tagToRemove) });
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  const deleteSchema = async (schema) => {
    if (!window.confirm(`Delete "${schema.name}"?`)) return;
    try { await api.delete(`/schemas/${schema.schema_id}`); toast.success('Deleted'); fetchData(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const downloadFullDictionary = async () => {
    try {
      const r = await api.get('/schemas/export/full-dictionary');
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'triomics_dictionary_json_schema.json'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed'); }
  };

  if (loading) {
    return (<div className="min-h-screen bg-white"><Navbar /><div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin" /></div></div>);
  }

  return (
    <div className="min-h-screen bg-white" data-testid="schemas-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'Chivo, sans-serif' }}>Schemas</h1>
            <p className="text-sm text-[#4B5563] mt-1">{schemas.length} schemas{selectedTag ? ` tagged "${selectedTag}"` : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadFullDictionary} className="rounded-none text-xs h-8" data-testid="download-full-dict-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export
            </Button>
            {canEdit && (
              <Button onClick={() => { setForm({ name: '', code: '', description: '', tags: [] }); setCreateOpen(true); }} className="rounded-none bg-[#0A0A0A] text-white hover:bg-[#171717] h-8 text-xs" data-testid="create-schema-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> New Schema
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="schema-filters">
          <div className="relative max-w-xs flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9CA3AF]" />
            <Input data-testid="schema-search" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-sm rounded-none" />
          </div>
          {allTags.length > 0 && (
            <div className="flex items-center gap-1">
              <Tag className="w-3 h-3 text-[#9CA3AF]" />
              <button onClick={() => setSelectedTag('')} className={`text-[10px] px-2 py-1 transition-colors ${!selectedTag ? 'bg-[#0A0A0A] text-white' : 'bg-zinc-100 text-[#4B5563]'}`} data-testid="tag-filter-all">All</button>
              {allTags.map(t => (
                <button key={t} onClick={() => setSelectedTag(selectedTag === t ? '' : t)} className={`text-[10px] px-2 py-1 transition-colors ${selectedTag === t ? 'bg-[#0A0A0A] text-white' : 'bg-zinc-100 text-[#4B5563]'}`} data-testid={`tag-filter-${t}`}>{t}</button>
              ))}
            </div>
          )}
        </div>

        {/* Table */}
        <div className="border border-[#E5E7EB]" data-testid="schemas-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F9FAFB] hover:bg-[#F9FAFB]">
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Name</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[120px]">Code</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[70px]">Entities</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[70px]">Attrs</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase tracking-wider">Tags</TableHead>
                {canEdit && <TableHead className="text-[10px] font-semibold uppercase tracking-wider w-[80px] text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {schemas.map(s => (
                <TableRow key={s.schema_id} className="cursor-pointer hover:bg-[#F9FAFB]" data-testid={`schema-row-${s.code}`}>
                  <TableCell onClick={() => navigate(`/schemas/${s.schema_id}`)}>
                    <div className="text-sm font-medium text-[#0A0A0A] hover:text-[#002FA7]">{s.name}</div>
                    {s.description && <p className="text-[10px] text-[#9CA3AF] mt-0.5 line-clamp-1 max-w-md">{s.description}</p>}
                  </TableCell>
                  <TableCell onClick={() => navigate(`/schemas/${s.schema_id}`)}>
                    <span className="text-xs text-[#4B5563]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{s.code}</span>
                  </TableCell>
                  <TableCell onClick={() => navigate(`/schemas/${s.schema_id}`)} className="text-xs text-[#4B5563]">{s.entity_count}</TableCell>
                  <TableCell onClick={() => navigate(`/schemas/${s.schema_id}`)} className="text-xs text-[#4B5563]">{s.attribute_count}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      {(s.tags || []).map(t => (
                        <span key={t} className="text-[9px] px-1.5 py-0.5 bg-purple-50 text-purple-700 font-medium flex items-center gap-0.5">
                          {t}
                          {canEdit && <button onClick={(e) => { e.stopPropagation(); removeTagFromSchema(s.schema_id, s.tags, t); }} className="hover:text-red-500"><X className="w-2.5 h-2.5" /></button>}
                        </span>
                      ))}
                      {canEdit && (
                        tagEditId === s.schema_id ? (
                          <form onSubmit={e => { e.preventDefault(); addTagToSchema(s.schema_id, s.tags || []); }} className="flex items-center gap-1">
                            <input value={tagEditInput} onChange={e => setTagEditInput(e.target.value)} autoFocus className="w-16 h-5 text-[10px] border border-[#E5E7EB] px-1" placeholder="tag" data-testid={`tag-input-${s.schema_id}`} />
                            <button type="submit" className="text-[#002FA7]"><Plus className="w-3 h-3" /></button>
                            <button type="button" onClick={() => setTagEditId(null)} className="text-[#9CA3AF]"><X className="w-3 h-3" /></button>
                          </form>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setTagEditId(s.schema_id); setTagEditInput(''); }} className="text-[10px] px-1 py-0.5 text-[#9CA3AF] hover:text-[#4B5563] border border-dashed border-[#E5E7EB]" data-testid={`add-tag-${s.schema_id}`}>
                            <Plus className="w-2.5 h-2.5 inline" /> tag
                          </button>
                        )
                      )}
                    </div>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 rounded-none text-red-500" onClick={(e) => { e.stopPropagation(); deleteSchema(s); }} data-testid={`delete-schema-${s.schema_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {schemas.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-sm text-[#9CA3AF]">No schemas found</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create Schema Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-none max-w-md" data-testid="create-schema-dialog">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>New Schema</DialogTitle><DialogDescription className="sr-only">Create a new schema</DialogDescription></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Name</Label>
              <div className="flex gap-2 mt-1">
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="rounded-none flex-1" data-testid="schema-name-input" placeholder="e.g., Ovarian Cancer" />
                <Button variant="outline" size="sm" onClick={handleAISuggest} disabled={suggesting || !form.name.trim()} className="rounded-none h-9 px-3 shrink-0" data-testid="schema-ai-suggest-btn">
                  {suggesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}<span className="ml-1.5 text-xs">AI</span>
                </Button>
              </div>
            </div>
            <div><Label className="text-xs font-semibold text-[#4B5563]">Code</Label><Input value={form.code} onChange={e => setForm(p => ({ ...p, code: e.target.value }))} className="rounded-none mt-1 font-mono" data-testid="schema-code-input" /></div>
            <div><Label className="text-xs font-semibold text-[#4B5563]">Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} className="rounded-none mt-1" data-testid="schema-desc-input" rows={2} /></div>
            <div>
              <Label className="text-xs font-semibold text-[#4B5563]">Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addFormTag())} className="rounded-none flex-1" data-testid="schema-tag-input" placeholder="e.g., ontada" />
                <Button variant="outline" size="sm" onClick={addFormTag} className="rounded-none h-9">Add</Button>
              </div>
              {form.tags.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{form.tags.map(t => (<span key={t} className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 flex items-center gap-1">{t}<button onClick={() => setForm(p => ({ ...p, tags: p.tags.filter(x => x !== t) }))}><X className="w-3 h-3" /></button></span>))}</div>}
            </div>
            <Button onClick={handleCreate} disabled={saving || !form.name.trim() || !form.code.trim()} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002585]" data-testid="save-schema-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> {saving ? 'Creating...' : 'Create Schema'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
